import React, {
  useState,
  useContext,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { UserContext } from "../../context";
import {
  Bell,
  BellRing,
  BellOff,
  Loader2,
  ArrowRight,
  Calendar,
  Clock,
  PlayCircle,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import {
  subscribeToLiveClass,
  unsubscribeFromLiveClass,
} from "../../services/notificationService";

// Background color mapping for different subjects (fallback if gradients aren't provided)
const subjectColors = {
  Chemistry: { start: "rgb(255, 87, 31)", end: "rgb(255, 138, 92)" },
  Physics: { start: "#2b86c5", end: "#5ab2f7" },
  Biology: { start: "#3da35a", end: "#8eda85" },
  Maths: { start: "rgb(251, 139, 35)", end: "rgb(255, 221, 0)" },
  English: { start: "#9c3fb4", end: "#c278d8" },
  Hindi: { start: "#e44858", end: "#ff7886" },
  "Social Studies": { start: "#2d7a78", end: "#5eb8b6" },
  "Computer Science": { start: "#5246c7", end: "#8075ff" },
};

// Default colors for subjects not in the mapping
const defaultColors = { start: "#6941c6", end: "#9f7aea" };

// Helper function to capitalize first letter of each word
const toTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export interface ChatLiveClassProps {
  id: string;
  title: string;
  subject: string;
  topic: string;
  instructor: string;
  start_time: string;
  duration: number;
  status: string;
  meeting_link: string;
  gradient_start?: string;
  gradient_end?: string;
  grade?: string;
}

export default function ChatLiveClassCard({
  classData,
}: {
  classData: ChatLiveClassProps;
}) {
  const {
    id,
    title,
    subject,
    topic,
    start_time,
    duration,
    meeting_link,
    gradient_start,
    gradient_end,
    grade = "class_9", // Default grade if not provided
    status = "initial", // Default status if not provided
  } = classData;

  // Get user from context
  const userContext = useContext(UserContext);
  const [user] = userContext?.user || [null];

  // State for notification status
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [isNotifying, setIsNotifying] = useState<boolean>(false);
  const [notificationSuccess, setNotificationSuccess] = useState<
    boolean | null
  >(null);
  const [notificationMessage, setNotificationMessage] = useState<string>("");

  // State for notification toast
  const [showToast, setShowToast] = useState<boolean>(false);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Format the date and time
  const classDate = new Date(start_time);
  const formattedDate = format(classDate, "MMM d, yyyy");
  const formattedTime = format(classDate, "h:mm a");

  // Determine status mappings
  const isPast = status === "disposed";
  const isLive = status === "live";
  const isUpcoming = status === "initial";

  // Process title and topic text to ensure proper capitalization
  const formattedTitle = toTitleCase(title);
  const formattedSubject = toTitleCase(subject);

  // Check subscription status on mount and when user changes
  useEffect(() => {
    // Skip if no user email or disposed class
    if (!user?.email || isPast) return;

    const checkSubscription = async () => {
      try {
        // Check localStorage first for better UX
        const localStatus = localStorage.getItem(
          `notification_${user.email}_${id}`
        );

        if (localStatus) {
          setIsSubscribed(localStatus === "true");
        } else {
          // API check
          const result = await fetch(
            `/api/notifications/status?email=${user.email}&sessionId=${id}`
          );
          const data = await result.json();
          setIsSubscribed(data.isSubscribed || false);

          // Save to localStorage for faster access next time
          localStorage.setItem(
            `notification_${user.email}_${id}`,
            data.isSubscribed ? "true" : "false"
          );
        }
      } catch (error) {
        console.error("Error checking subscription status:", error);
      }
    };

    checkSubscription();
  }, [user?.email, id, isPast]);

  // Show notification toast with smooth animation
  const showNotification = useCallback(
    (message: string, success: boolean | null = null) => {
      setNotificationMessage(message);
      setNotificationSuccess(success);
      setShowToast(true);

      // Clear any existing timeout
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }

      // Auto-hide after 3 seconds
      toastTimeoutRef.current = setTimeout(() => {
        setShowToast(false);
      }, 3000);
    },
    []
  );

  // Use gradient colors from backend if available, otherwise fall back to subject-based colors
  let gradientStyle;
  if (gradient_start && gradient_end) {
    gradientStyle = {
      background: `linear-gradient(to right, ${gradient_start}, ${gradient_end})`,
    };
  } else {
    // Fall back to the subject color mapping if gradients aren't provided
    const colors =
      subjectColors[subject as keyof typeof subjectColors] || defaultColors;
    gradientStyle = {
      background: `linear-gradient(to right, ${colors.start}, ${colors.end})`,
    };
  }

  // Handle notification toggle
  const handleNotify = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      // Prevent multiple clicks
      if (isNotifying || isPast) return;

      setIsNotifying(true);

      // Check if user is logged in and has email
      if (!user || !user.email || !user.uid) {
        showNotification("Please log in to receive notifications", false);
        setIsNotifying(false);
        return;
      }

      // Toggle subscription status
      if (isSubscribed) {
        // Unsubscribe
        const result = await unsubscribeFromLiveClass(user.email, id, grade, {
          userId: user.uid,
          name: user.displayName || undefined,
        });

        if (result.success) {
          setIsSubscribed(false);
          showNotification("Notifications turned off for this class", true);
          localStorage.setItem(`notification_${user.email}_${id}`, "false");
        } else {
          throw new Error(result.message || "Failed to unsubscribe");
        }
      } else {
        // Subscribe
        const result = await subscribeToLiveClass(user.email, id, grade, {
          userId: user.uid,
          name: user.displayName || undefined,
        });

        if (result.success) {
          setIsSubscribed(true);
          showNotification("You'll be notified before this class starts", true);
          localStorage.setItem(`notification_${user.email}_${id}`, "true");
        } else {
          throw new Error(result.message || "Failed to subscribe");
        }
      }
    } catch (error) {
      console.error("Error toggling notifications:", error);
      showNotification("An unexpected error occurred", false);
    } finally {
      setIsNotifying(false);
    }
  };

  return (
    <div className="chat-live-class-card relative rounded-md overflow-hidden shadow-sm border border-gray-700 transition-all hover:shadow-md mb-2">
      {/* Header with gradient background */}
      <div
        className="h-16 flex items-center px-3 text-white relative"
        style={gradientStyle}
      >
        <div className="overflow-hidden">
          <h3 className="font-bold text-sm mb-0.5 text-white truncate">
            {formattedTitle}
          </h3>
          <p className="text-xs text-white opacity-90 font-medium truncate">
            {formattedSubject}
          </p>
        </div>

        {/* Status badge */}
        <div
          className={`status-badge status-${
            isPast ? "ended" : isLive ? "live" : "upcoming"
          }`}
        >
          {isLive ? (
            <>
              <span className="pulse-dot"></span> Live Now
            </>
          ) : isPast ? (
            "Recording Available"
          ) : (
            "Upcoming"
          )}
        </div>
      </div>

      {/* Class information */}
      <div className="p-3 bg-gray-800">
        <div className="flex flex-col space-y-1.5">
          <div className="flex items-center text-xs text-gray-300">
            <Calendar size={14} className="mr-1.5 flex-shrink-0" />
            <span>{formattedDate}</span>
          </div>
          <div className="flex items-center text-xs text-gray-300">
            <Clock size={14} className="mr-1.5 flex-shrink-0" />
            <span>
              {formattedTime} • {duration} mins
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 mt-3">
          {/* Notification button */}
          {!isPast && (
            <button
              onClick={handleNotify}
              type="button"
              className={`notification-btn flex items-center justify-center h-8 flex-shrink-0 rounded-md transition-all duration-300 ${
                isNotifying
                  ? "bg-indigo-700 text-white"
                  : isSubscribed
                  ? "bg-green-800 text-green-300 hover:bg-green-700"
                  : "bg-gray-700 hover:bg-gray-600 text-gray-300"
              }`}
              disabled={isNotifying || isPast}
              aria-label={
                isPast
                  ? "Class already ended"
                  : isSubscribed
                  ? "Turn off notifications for this class"
                  : "Get notified"
              }
              title={
                isPast
                  ? "Class already ended"
                  : isSubscribed
                  ? "Turn off notifications for this class"
                  : "Get notified"
              }
            >
              {isNotifying ? (
                <div className="flex items-center justify-center w-full h-full">
                  <Loader2 size={16} className="animate-spin text-white" />
                </div>
              ) : isPast ? (
                <BellOff size={16} className="bell-icon disabled" />
              ) : isSubscribed ? (
                <BellRing size={16} className="bell-icon subscribed" />
              ) : (
                <Bell size={16} className="bell-icon" />
              )}
            </button>
          )}

          {/* Join/Recording button */}
          <a
            href={meeting_link}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex-grow flex items-center justify-center h-8 px-3 rounded-md text-sm font-medium transition-colors duration-300 ${
              isPast
                ? "bg-purple-600 hover:bg-purple-700 text-white"
                : isLive
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-indigo-600 hover:bg-indigo-700 text-white"
            }`}
          >
            {isPast ? (
              <>
                <span>Watch Recording</span>
                <PlayCircle size={14} className="ml-1" />
              </>
            ) : isLive ? (
              <>
                <span>Join Now</span>
                <ArrowRight size={14} className="ml-1" />
              </>
            ) : (
              "Join Class"
            )}
          </a>
        </div>

        {/* Notification toast */}
        {showToast && (
          <div className="notification-toast mt-2">
            <div
              className={`flex items-center py-1 px-2 rounded text-xs ${
                notificationSuccess === false
                  ? "bg-red-900/50 border border-red-700 text-red-300"
                  : notificationSuccess
                  ? "bg-green-900/50 border border-green-700 text-green-300"
                  : "bg-blue-900/50 border border-blue-700 text-blue-300"
              }`}
            >
              {notificationSuccess === false ? (
                <BellOff size={12} className="mr-1.5" />
              ) : notificationSuccess ? (
                <BellRing size={12} className="mr-1.5" />
              ) : (
                <Bell size={12} className="mr-1.5" />
              )}
              <span>{notificationMessage}</span>
            </div>
          </div>
        )}
      </div>

      {/* Styles */}
      <style jsx>{`
        .chat-live-class-card {
          max-width: 340px;
          width: 100%;
        }

        .notification-btn {
          width: 40px;
          padding: 0;
        }

        .status-badge {
          position: absolute;
          top: 6px;
          right: 6px;
          padding: 2px 6px;
          border-radius: 10px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: flex;
          align-items: center;
        }

        .status-badge.status-live {
          background-color: rgba(16, 185, 129, 0.8);
          color: white;
        }

        .status-badge.status-ended {
          background-color: rgba(156, 163, 175, 0.8);
          color: white;
        }

        .status-badge.status-upcoming {
          background-color: rgba(79, 70, 229, 0.8);
          color: white;
        }

        .pulse-dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          margin-right: 5px;
          background-color: #fff;
          border-radius: 50%;
          animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .bell-icon.disabled {
          color: #6b7280;
          opacity: 0.6;
        }

        .bell-icon.subscribed {
          color: #a7f3d0;
          animation: bellRing 1s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
          transform-origin: top center;
        }

        @keyframes bellRing {
          0%,
          100% {
            transform: rotate(0);
          }
          15% {
            transform: rotate(15deg);
          }
          30% {
            transform: rotate(-10deg);
          }
          45% {
            transform: rotate(5deg);
          }
          60% {
            transform: rotate(-5deg);
          }
          75% {
            transform: rotate(2deg);
          }
        }

        .notification-toast {
          opacity: 0;
          transform: translateY(10px);
          animation: fadeInUp 0.3s ease forwards;
        }

        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
